import React, { useState, useEffect } from "react";

/**
 * Reusable notification component for API responses
 * @param {string} type - "success" or "error"
 * @param {string} message - The notification message to display
 * @param {number} duration - Duration in milliseconds before auto-dismiss (default: 2000ms)
 * @returns Notification component with proper styling based on type
 */
const Notification = ({ type, message, duration = 2000 }) => {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (!message) return;

    // Reset visibility when message changes
    setVisible(true);

    // Auto-dismiss after duration
    const timer = setTimeout(() => {
      setVisible(false);
    }, duration);

    // Clear timeout on unmount or when message changes
    return () => clearTimeout(timer);
  }, [message, duration]);

  if (!message || !visible) return null;

  const isSuccess = type === "success";

  return (
    <div
      className={`flex items-center gap-2 ${
        isSuccess
          ? "bg-green-50 border-l-4 border-green-500 text-green-700"
          : "bg-red-50 border-l-4 border-red-500 text-red-700"
      } p-4 rounded-md mt-6 max-w-md transition-opacity duration-300`}
    >
      {isSuccess ? (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
            clipRule="evenodd"
          />
        </svg>
      ) : (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
            clipRule="evenodd"
          />
        </svg>
      )}
      <p className="text-sm font-medium">{message}</p>
    </div>
  );
};

export default Notification;
